import type { CalendarFeed } from "@ronin/contracts";
import { Button, calendarColors, cn } from "@ronin/ui";
import { Eye, EyeOff } from "lucide-react";
import { useFeedMutations } from "./use-calendar.js";

/** Shows or hides a feed's events at once (spec03 FR10a). */
export function FeedEye({ feed }: { feed: CalendarFeed }) {
  const { setVisible } = useFeedMutations();
  const Icon = feed.visible ? Eye : EyeOff;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-pressed={!feed.visible}
      aria-label={`${feed.visible ? "Hide" : "Show"} ${feed.name}`}
      title={feed.visible ? "Hide" : "Show"}
      onClick={() => setVisible.mutate({ feed, visible: !feed.visible })}
    >
      <Icon className={cn(!feed.visible && "text-faint-foreground")} />
    </Button>
  );
}

/** Compact feed list under the calendar header: swatch, name and eye per feed. */
export function FeedBar({ feeds }: { feeds: readonly CalendarFeed[] }) {
  if (feeds.length === 0) return null;
  return (
    <ul aria-label="Feed visibility" className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {feeds.map((feed) => (
        <li key={feed.id} className="flex items-center gap-1.5">
          <span
            className={cn(
              "size-2.5 shrink-0",
              calendarColors[feed.color].swatch,
              !feed.visible && "opacity-40",
            )}
            aria-hidden
          />
          <span
            className={cn(
              "max-w-40 truncate text-xs",
              feed.visible ? "text-secondary-foreground" : "text-faint-foreground line-through",
            )}
          >
            {feed.name}
          </span>
          <FeedEye feed={feed} />
        </li>
      ))}
    </ul>
  );
}
